/**
 * NavList Component
 *
 * Data-driven navigation menu that renders menu items based on menuConfig.js.
 * Uses inline NavMenuItem and NavMenuCategory components for consistent styling.
 *
 * Refactored from 2,131 lines to ~300 lines using data-driven approach.
 */

import React, { useLayoutEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Box,
  Tooltip,
  Collapse,
} from '@mui/material';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import HomeIcon from '@mui/icons-material/HomeTwoTone';
import CircleIcon from '@mui/icons-material/Circle';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';

// Icons
import ProcessingIcon from '@mui/icons-material/PrecisionManufacturing';
import FacilityIcon from 'mdi-material-ui/Factory';
import InventoryIcon from 'mdi-material-ui/BallotRecount';
import QAIcon from 'mdi-material-ui/CheckboxMultipleMarkedOutline';
import CRMIcon from 'mdi-material-ui/AccountGroupOutline';
import SecurityIcon from 'mdi-material-ui/Security';
import ProductionIcon from '@mui/icons-material/AccessTimeTwoTone';
import DashMenuIcon from 'mdi-material-ui/MonitorDashboard';
import DocumentsIcon from '@mui/icons-material/FileCopyTwoTone';
import TimelapseTwoToneIcon from '@mui/icons-material/TimelapseTwoTone';
import InvestigationsIcon from '@mui/icons-material/FindInPageTwoTone';
import CultivationIcon from 'mdi-material-ui/Grass';
import SettingsIcon from '@mui/icons-material/SettingsTwoTone';
import ReportingIcon from '@mui/icons-material/BarChartTwoTone';
import HealthDashboardIcon from '@mui/icons-material/MonitorHeartTwoTone';
import ComplianceEvidenceIcon from '@mui/icons-material/VerifiedUserTwoTone';
import AnnouncementNavIcon from 'mdi-material-ui/BullhornVariant';
import EmailTemplatesIcon from '@mui/icons-material/EmailTwoTone';
import EmailsSentIcon from '@mui/icons-material/DraftsTwoTone';
import HelpLibraryIcon from '@mui/icons-material/AutoStoriesTwoTone';
import DataUploadIcon from 'mdi-material-ui/FileUpload';
import DispensaryIcon from '@mui/icons-material/StorefrontTwoTone';
import AdminLogsIcon from '@mui/icons-material/HistoryTwoTone';
import ManagementIcon from 'mdi-material-ui/AccountNetworkOutline';
import QueueJobsIcon from '@mui/icons-material/BrowseGalleryTwoTone';
import ScreenDefsIcon from '@mui/icons-material/DvrTwoTone';

// Custom components
import Icon from '../../UI/Icons/Icons';
import { useNavItemStyles } from '../../../hooks/useNavItemStyles';
import { useCategoryStyles } from '../../../hooks/useCategoryStyles';

// Menu configuration
import {
  STANDALONE_ITEMS,
  MENU_CATEGORIES,
  INTERNAL_ADMIN_ITEMS,
  GLOBAL_ITEMS,
  shouldShowItem,
  shouldShowCategory,
} from './menuConfig';

// Actions
import { exitTenant } from '../../../actions/authentication';

// Icon mapping from string keys to actual components
const ICON_MAP = {
  DashMenuIcon,
  CultivationIcon,
  ProcessingIcon,
  FacilityIcon,
  InventoryIcon,
  QAIcon,
  CRMIcon,
  ReportingIcon,
  ProductionIcon,
  DocumentsIcon,
  SettingsIcon,
  SecurityIcon,
  TimelapseTwoToneIcon,
  InvestigationsIcon,
  HealthDashboardIcon,
  ComplianceEvidenceIcon,
  AnnouncementNavIcon,
  EmailTemplatesIcon,
  EmailsSentIcon,
  HelpLibraryIcon,
  DataUploadIcon,
  DispensaryIcon,
  AdminLogsIcon,
  ManagementIcon,
  QueueJobsIcon,
  ScreenDefsIcon,
};

/**
 * NavMenuItem Component (inline)
 *
 * A reusable navigation menu item that supports:
 * - Theme-aware styling via useNavItemStyles hook
 * - Selection state with active indicator
 * - Icon display with tooltips
 * - Multiple nesting levels
 */
const NavMenuItem = ({
  name,
  optionKey,
  path,
  menuIndex,
  titleKey,
  screenDef,
  screenDefs,
  Icon: IconComponent,
  isMobile = false,
  isDrawerOpen = true,
  nestingLevel = 0,
  availableMenu,
  menuState,
  buttonClick,
  t,
  renderIcon,
  dataCy,
}) => {
  const styles = useNavItemStyles({ isOpen: isDrawerOpen, nestingLevel });

  // Check if this menu option is available
  if (availableMenu && !availableMenu.options.includes(optionKey)) {
    return null;
  }

  const isSelected = menuState?.index === menuIndex;
  const title = t ? t(titleKey) : titleKey;

  const handleClick = (e) => {
    if (buttonClick) {
      buttonClick(e, path);
    }
  };

  // Get typography variant and weight based on nesting level
  const getTypographyVariant = () => {
    switch (nestingLevel) {
      case 0:
        return 'body1';
      case 1:
        return 'body2';
      case 2:
        return 'subtitle2';
      default:
        return 'body2';
    }
  };

  const getTypographyWeight = () => {
    // All menu items use regular weight (400) to match category styling
    return 400;
  };

  // Render the icon
  const renderMenuIcon = () => {
    if (renderIcon) {
      return renderIcon({ isSelected, styles });
    }

    // Direct Icon component passed
    if (IconComponent) {
      return (
        <Tooltip title={title} placement="right" disableHoverListener={isDrawerOpen}>
          <IconComponent sx={styles.icon(isSelected)} />
        </Tooltip>
      );
    }

    // If using screenDefs Icon component pattern
    if (screenDef && screenDefs && Object.keys(screenDefs).length > 0) {
      return (
        <Icon
          sx={styles.icon(isSelected)}
          screenDefs={screenDefs}
          screenDef={screenDef}
          title={title}
          placement="right"
        />
      );
    }

    // Fallback: small bullet icon for nested items without defined icon
    if (nestingLevel > 0) {
      return (
        <Tooltip title={title} placement="right" disableHoverListener={isDrawerOpen}>
          <CircleIcon
            sx={{
              ...styles.icon(isSelected),
              fontSize: '8px',
              opacity: 0.5,
            }}
          />
        </Tooltip>
      );
    }

    return null;
  };

  return (
    <ListItemButton
      name={name}
      dense={!isMobile}
      sx={styles.listItem}
      onClick={handleClick}
      selected={isSelected}
      data-cy={dataCy || name}
    >
      <ListItemIcon sx={{ minWidth: isDrawerOpen ? 40 : 'auto', justifyContent: 'center' }}>
        {renderMenuIcon()}
      </ListItemIcon>
      <ListItemText
        disableTypography
        primary={
          <Typography variant={getTypographyVariant()} sx={{ fontWeight: getTypographyWeight() }}>
            {title}
          </Typography>
        }
      />
    </ListItemButton>
  );
};

/**
 * NavMenuCategory Component (inline)
 *
 * A reusable navigation menu category (expandable section) that supports:
 * - Theme-aware styling via useCategoryStyles hook
 * - Expand/collapse with smooth animation
 * - Active state when child items are selected
 * - Icon display with tooltips
 * - Nested sub-categories
 * - Auto-expands drawer when clicked in collapsed mode
 */
const NavMenuCategory = ({
  categoryKey,
  titleKey,
  Icon: IconComponent,
  isMobile = false,
  isDrawerOpen = true,
  nestingLevel = 0,
  availableMenu,
  menuState,
  handleMenuClick,
  handleSubMenuClick,
  onExpandDrawer,
  t,
  children,
  isVisible = true,
}) => {
  const styles = useCategoryStyles({ isDrawerOpen, nestingLevel });

  // Check if this category should be visible
  if (!isVisible) {
    return null;
  }

  // Check if category is available in menu
  if (availableMenu && !availableMenu.categories.includes(categoryKey)) {
    return null;
  }

  const isMain = nestingLevel === 0;
  const isExpanded = isMain
    ? menuState?.openMenu === categoryKey && menuState?.isOpen
    : menuState?.openSubMenu === categoryKey && menuState?.subIsOpen;
  const isActive = menuState?.category === categoryKey;

  const handleClick = () => {
    // Always allow expand/collapse - works in both collapsed and expanded drawer states
    if (isMain) {
      handleMenuClick?.(categoryKey);
    } else {
      handleSubMenuClick?.(categoryKey);
    }
  };

  const title = t ? t(titleKey) : titleKey;

  // Get typography variant based on nesting level
  const getTypographyVariant = () => {
    return isMain ? 'body1' : 'body2';
  };

  return (
    <>
      <ListItemButton
        name={categoryKey}
        data-cy={categoryKey}
        dense={!isMobile}
        sx={styles.categoryButton}
        onClick={handleClick}
        className={isActive ? 'active-category' : ''}
      >
        <ListItemIcon sx={{ ...styles.categoryIcon(isActive), minWidth: isDrawerOpen ? 40 : 'auto', justifyContent: 'center' }}>
          <Tooltip title={title} placement="right" disableHoverListener={isDrawerOpen}>
            <IconComponent color={isActive ? 'secondary' : 'primary'} />
          </Tooltip>
        </ListItemIcon>
        <ListItemText
          disableTypography
          primary={
            <Typography variant={getTypographyVariant()} sx={isMain ? styles.categoryText : styles.subCategoryText}>
              {title}
            </Typography>
          }
        />
        {/* Show chevron in both expanded and collapsed drawer states */}
        {isExpanded ? (
          <ExpandLess sx={{
            ...styles.expandIcon(true),
            fontSize: isDrawerOpen ? '1.25rem' : '0.875rem',
            ml: isDrawerOpen ? 0 : -0.5,
          }} />
        ) : (
          <ExpandMore sx={{
            ...styles.expandIcon(false),
            fontSize: isDrawerOpen ? '1.25rem' : '0.875rem',
            ml: isDrawerOpen ? 0 : -0.5,
          }} />
        )}
      </ListItemButton>

      {/* Collapse for nested items - works in both drawer states */}
      <Collapse in={isExpanded} {...styles.collapseProps}>
        <List sx={styles.nestedList}>
          {children}
        </List>
      </Collapse>
    </>
  );
};

export const NavList = (props) => {
  const { isMobile, open, onExpandDrawer } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const styles = useNavItemStyles({ isOpen: open, nestingLevel: 0 });

  // Redux state
  const menuState = useSelector((state) => state.navMenu);
  const menuOptions = useSelector((state) => state.security.menuOptions);
  const auth = useSelector((state) => state.auth);
  const screenDefs = useSelector((state) => state.auth.screenDefs);

  const [availableMenu, setAvailableMenu] = useState({
    options: [],
    categories: [],
  });

  useLayoutEffect(() => {
    if (menuOptions.length !== 0) {
      let categories = [];
      let groups = [];
      menuOptions.forEach((option) => {
        if (!groups.includes(option.name)) {
          groups.push(option.name);
          if (!categories.includes(option.category)) {
            categories.push(option.category);
          }
        }
      });

      setAvailableMenu({
        options: groups,
        categories: categories,
      });
    }
  }, [menuOptions]);

  const handleMenuClick = (name) => {
    if (menuState.openMenu !== name) {
      dispatch({ type: 'OPEN_NEW_NAVMENU', payload: name });
    } else {
      dispatch({ type: 'TOGGLE_NAVMENU' });
    }
  };

  const handleSubMenuClick = (name) => {
    if (menuState.openSubMenu !== name) {
      dispatch({ type: 'OPEN_NEW_NAVSUBMENU', payload: name });
    } else {
      dispatch({ type: 'TOGGLE_NAVSUBMENU' });
    }
  };

  const handleHomeClick = (e, path) => {
    dispatch({ type: 'RESET_NAVMENU' });
    buttonClick(e, path);
  };

  const buttonClick = (e, path) => {
    e.preventDefault();
    window.location.replace(path);
  };

  // Common props for all menu items
  const commonItemProps = {
    isMobile,
    isDrawerOpen: open,
    availableMenu,
    menuState,
    screenDefs,
    t,
  };

  // Common props for all categories
  const commonCategoryProps = {
    isMobile,
    isDrawerOpen: open,
    availableMenu,
    menuState,
    handleMenuClick,
    handleSubMenuClick,
    onExpandDrawer,
    t,
  };

  // Render Home button at the top (always visible)
  const renderHomeButton = () => {
    const isSelected = menuState?.index === 'home';
    // Route to admin landing for internal admins, tenant home for regular users
    const homePath = auth.isInternalAdmin ? '/#/admin' : '/#/home';

    return (
      <NavMenuItem
        key="home"
        name="home"
        optionKey="home"
        path={homePath}
        menuIndex="home"
        titleKey="home"
        Icon={HomeIcon}
        buttonClick={handleHomeClick}
        nestingLevel={0}
        {...commonItemProps}
        // Always show home - bypass availableMenu check
        availableMenu={{ options: ['home'], categories: ['home'] }}
      />
    );
  };

  // Render standalone items (LaunchPad, Dashboards - Home is separate, Settings renders at bottom)
  const renderStandaloneItems = () => {
    return STANDALONE_ITEMS.filter(item => item.key !== 'home' && item.key !== 'admin').map((item) => {
      // Check visibility conditions
      if (!shouldShowItem(item, auth)) return null;

      // For standalone items, show if either:
      // 1. The screenDef exists (user has permission to this screen), OR
      // 2. The category is in availableMenu.categories (traditional check)
      const hasScreenAccess = screenDefs && screenDefs[item.screenDef];
      const hasCategoryAccess = availableMenu.categories.includes(item.category);
      if (!hasScreenAccess && !hasCategoryAccess) return null;

      const handleClick = item.resetNav ? handleHomeClick : buttonClick;
      // Get icon component if defined in config
      const ItemIcon = item.icon ? ICON_MAP[item.icon] : undefined;

      return (
        <NavMenuItem
          key={item.key}
          name={item.key}
          optionKey={item.key}
          path={item.path}
          menuIndex={item.index}
          titleKey={item.titleKey}
          screenDef={screenDefs?.[item.screenDef]}
          Icon={ItemIcon}
          buttonClick={handleClick}
          nestingLevel={0}
          {...commonItemProps}
          // If user has screen access, bypass availableMenu check
          availableMenu={hasScreenAccess ? { options: [item.key], categories: [item.category] } : availableMenu}
        />
      );
    });
  };

  // Render menu items within a category
  const renderMenuItems = (items, nestingLevel = 1) => {
    return items.map((item) => {
      if (!shouldShowItem(item, auth)) return null;

      return (
        <NavMenuItem
          key={item.key}
          name={item.key.toLowerCase()}
          optionKey={item.key}
          path={item.path}
          menuIndex={item.index}
          titleKey={item.titleKey}
          screenDef={screenDefs?.[item.screenDef]}
          buttonClick={buttonClick}
          nestingLevel={nestingLevel}
          dataCy={item.dataCy}
          {...commonItemProps}
        />
      );
    });
  };

  // Render sub-categories within a category
  const renderSubCategories = (subCategories) => {
    return subCategories.map((subCat) => {
      const SubIcon = ICON_MAP[subCat.icon];
      if (!shouldShowCategory(subCat, availableMenu, auth)) return null;

      return (
        <NavMenuCategory
          key={subCat.key}
          categoryKey={subCat.key}
          titleKey={subCat.titleKey}
          Icon={SubIcon}
          nestingLevel={1}
          {...commonCategoryProps}
        >
          {renderMenuItems(subCat.items, 2)}
        </NavMenuCategory>
      );
    });
  };

  // Render main menu categories
  const renderCategories = () => {
    return MENU_CATEGORIES.map((category) => {
      if (!shouldShowCategory(category, availableMenu, auth)) return null;

      const CategoryIcon = ICON_MAP[category.icon];

      // Determine rendering order based on subCategoriesFirst flag
      const renderItems = () => category.items && renderMenuItems(category.items, 1);
      const renderSubs = () => category.subCategories && renderSubCategories(category.subCategories);

      return (
        <NavMenuCategory
          key={category.key}
          categoryKey={category.key}
          titleKey={category.titleKey}
          Icon={CategoryIcon}
          nestingLevel={0}
          {...commonCategoryProps}
        >
          {category.subCategoriesFirst ? (
            <>
              {/* Sub-categories first, then direct items */}
              {renderSubs()}
              {renderItems()}
            </>
          ) : (
            <>
              {/* Direct items first (default), then sub-categories */}
              {renderItems()}
              {renderSubs()}
            </>
          )}
        </NavMenuCategory>
      );
    });
  };

  // Render exit tenant button (for internal users viewing a tenant)
  // Pinned to the very bottom of the nav
  const renderExitTenant = () => {
    // Impersonated sessions carry the target user's own `internal` flag (usually false),
    // not the acting admin's - so isImpersonating is checked independently here, otherwise
    // this item (the only way back to the admin session) would never render for them.
    const isImpersonating = auth.isImpersonating === true;
    if ((!auth.user?.internal || auth.isInternalAdmin) && !isImpersonating) return null;

    return (
      <Box sx={{ pt: 0.5, pb: 1 }}>
        <Divider sx={{ mx: open ? 2 : 1, mb: 0.75 }} />
        <ListItemButton
          dense={!isMobile}
          name="exitTenant"
          sx={{
            borderRadius: open ? '20px' : '12px',
            margin: open ? theme.spacing(0.25, 1.5) : theme.spacing(0.25, 0.5),
            padding: open ? theme.spacing(0.5, 2) : theme.spacing(0.5),
            justifyContent: open ? 'flex-start' : 'center',
            minHeight: open ? 'auto' : 36,
            backgroundColor: theme.palette.mode === 'dark'
              ? 'rgba(255, 100, 100, 0.08)'
              : 'rgba(211, 47, 47, 0.04)',
            border: open ? '1px dashed' : 'none',
            borderColor: theme.palette.mode === 'dark'
              ? 'rgba(255, 100, 100, 0.3)'
              : 'rgba(211, 47, 47, 0.3)',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark'
                ? 'rgba(255, 100, 100, 0.15)'
                : 'rgba(211, 47, 47, 0.08)',
              transform: open ? 'translateX(2px)' : 'scale(1.05)',
            },
          }}
          onClick={() => dispatch(exitTenant(auth.user._id))}
        >
          <ListItemIcon sx={{ minWidth: open ? 40 : 24, justifyContent: 'center' }}>
            <LogoutRoundedIcon sx={{ color: theme.palette.error.main, fontSize: open ? '1.15rem' : '1.05rem' }} />
          </ListItemIcon>
          {open && (
            <ListItemText
              disableTypography
              primary={
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.error.main,
                    fontWeight: 500,
                  }}
                >
                  {t(isImpersonating ? 'exitImpersonation' : 'exitTenant')}
                </Typography>
              }
            />
          )}
        </ListItemButton>
      </Box>
    );
  };

  // Render Settings item (at bottom of menu, before internal admin items)
  const renderSettingsItem = () => {
    const settingsItem = STANDALONE_ITEMS.find(item => item.key === 'admin');
    if (!settingsItem) return null;

    // Check visibility conditions
    if (!shouldShowItem(settingsItem, auth)) return null;

    // Check access
    const hasScreenAccess = screenDefs && screenDefs[settingsItem.screenDef];
    const hasCategoryAccess = availableMenu.categories.includes(settingsItem.category);
    if (!hasScreenAccess && !hasCategoryAccess) return null;

    const ItemIcon = settingsItem.icon ? ICON_MAP[settingsItem.icon] : undefined;

    return (
      <NavMenuItem
        key={settingsItem.key}
        name={settingsItem.key}
        optionKey={settingsItem.key}
        path={settingsItem.path}
        menuIndex={settingsItem.index}
        titleKey={settingsItem.titleKey}
        screenDef={screenDefs?.[settingsItem.screenDef]}
        Icon={ItemIcon}
        buttonClick={buttonClick}
        nestingLevel={0}
        {...commonItemProps}
        availableMenu={hasScreenAccess ? { options: [settingsItem.key], categories: [settingsItem.category] } : availableMenu}
      />
    );
  };

  // Render internal admin items
  const renderInternalAdminItems = () => {
    if (!auth.isInternalAdmin) return null;

    const allInternalItems = [
      ...INTERNAL_ADMIN_ITEMS,
      ...GLOBAL_ITEMS.filter((item) => !item.internalOnly || auth.isInternalAdmin),
      // Additional internal-only items
      { key: 'Tenants', path: '/#/tenants', index: 'tenants', titleKey: 'tenants', screenDef: 'TenantList' },
      { key: 'Users', path: '/#/users', index: 'users', titleKey: 'users', screenDef: 'UserList' },
    ];

    return (
      <List>
        {allInternalItems.map((item) => {
          const ItemIcon = item.icon ? ICON_MAP[item.icon] : undefined;
          return (
            <NavMenuItem
              key={item.key}
              name={item.key.toLowerCase()}
              optionKey={item.key}
              path={item.path}
              menuIndex={item.index}
              titleKey={item.titleKey}
              screenDef={screenDefs?.[item.screenDef]}
              Icon={ItemIcon}
              buttonClick={buttonClick}
              nestingLevel={0}
              {...commonItemProps}
              // Skip availableMenu check for internal admin items
              availableMenu={{ options: [item.key], categories: [] }}
            />
          );
        })}
      </List>
    );
  };

  return (
    <List
      component="nav"
      sx={{
        width: '100%',
        maxWidth: '360px',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        pb: 0,
      }}
    >
      {/* Scrollable menu content */}
      <Box sx={{ flex: 1 }}>
        {/* Home always at top */}
        {renderHomeButton()}
        {/* Other standalone items (LaunchPad, Dashboards) */}
        {renderStandaloneItems()}
        {/* Main categories */}
        {renderCategories()}
        {/* Settings at bottom of categories */}
        {renderSettingsItem()}
        {/* Internal admin items */}
        {renderInternalAdminItems()}
      </Box>
      {/* Exit Tenant pinned to bottom */}
      {renderExitTenant()}
    </List>
  );
};

export default NavList;
